/**
 * "Get Pro" pressed while signed out, remembered across the sign-in dialog so that signing in
 * can carry on to checkout.
 *
 * It used to be a bare `{ plan, interval }` in localStorage that only a sign-in ever removed.
 * Close the dialog instead and it stayed — through log-outs, browser restarts and days — until
 * the NEXT sign-in, from any page, sent that person to Stripe (2026-10-10: a login from the home
 * page landed on the billing portal; the second login was normal because the first used it up).
 * So it is now stamped and short-lived, and closing the dialog drops it (AuthLauncher).
 */

const KEY = 'billingIntent';

/** Long enough to create an account or fetch a reset email and come back; nowhere near "later". */
export const BILLING_INTENT_TTL_MS = 10 * 60 * 1000;

export type BillingInterval = 'monthly' | 'yearly';
export type BillingIntent = { plan: 'pro'; interval: BillingInterval };

export function saveBillingIntent(interval: BillingInterval, now = Date.now()): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ plan: 'pro', interval, savedAt: now }));
  } catch (_e) {
    // No storage: signing in simply won't continue to checkout, and Get Pro still works after.
  }
}

export function clearBillingIntent(): void {
  try {
    localStorage.removeItem(KEY);
  } catch (_e) {
    // Nothing stored that could be read back either.
  }
}

/**
 * The saved intent, if it is still fresh. Removes it either way: one sign-in gets one chance.
 *
 * An entry without `savedAt` was written before the stamp existed, which makes it exactly the
 * stale click this module exists to stop — those are dropped, not honoured.
 */
export function takeBillingIntent(now = Date.now()): BillingIntent | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(KEY);
  } catch (_e) {
    return null;
  }
  if (raw === null) return null;
  clearBillingIntent();

  try {
    const { plan, interval, savedAt } = JSON.parse(raw) ?? {};
    if (plan !== 'pro') return null;
    if (interval !== 'monthly' && interval !== 'yearly') return null;
    if (typeof savedAt !== 'number') return null;
    const age = now - savedAt;
    // A stamp from the future means the clock moved; there is no telling how old it really is.
    if (age < 0 || age > BILLING_INTENT_TTL_MS) return null;
    return { plan, interval };
  } catch (_e) {
    return null;
  }
}
